import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import useODOCommentCreate from "../../hooks/cs/useODOCommentCreate";
import { toB64Obj, toDataUri } from "../../utils/fileUtils";
import { ImageIcon, Send, Upload, X } from "lucide-react";
import { DebouncedInput } from "../DebouncedInput";

interface ODOCommentFormProps {
  inquiryId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  description: string;
}

interface ImageData {
  file: File;
  base64: string;
  preview: string;
}

const ODOCommentForm = ({
  inquiryId,
  onSuccess,
  onCancel,
}: ODOCommentFormProps) => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { createODOComment, isLoading, error, clearError } =
    useODOCommentCreate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      description: "",
    },
  });

  const watchedDescription = watch("description", "");

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    clearError();

    try {
      const newImages: ImageData[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith("image/")) {
          console.warn(`${file.name}은(는) 이미지 파일이 아닙니다.`);
          continue;
        }

        try {
          const result = await toB64Obj(file);
          const preview = toDataUri(result);

          newImages.push({
            file,
            base64: result.data,
            preview,
          });
        } catch (error) {
          console.error(`이미지 변환 실패: ${file.name}`, error);
        }
      }

      if (newImages.length > 0) {
        setImages(prev => [...prev, ...newImages]);
      }
    } catch (error) {
      console.error("이미지 업로드 오류:", error);
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FormData) => {
    clearError();

    const success = await createODOComment({
      inquiry_id: inquiryId,
      description: data.description.trim(),
      reference_image: images.length > 0 ? images.map(img => img.base64).join(",") : "",
    });

    if (success) {
      reset();
      setImages([]);
      onSuccess();
    }
  };

  const handleCancel = () => {
    reset();
    setImages([]);
    clearError();
    onCancel();
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">답글 작성</h3>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            답글 내용 <span className="text-red-500">*</span>
          </label>
          <Controller
            name="description"
            control={control}
            rules={{
              required: "답글 내용을 입력해주세요.",
              maxLength: {
                value: 1000,
                message: "답글은 최대 1000자까지 입력 가능합니다.",
              },
            }}
            render={({ field }) => (
              <DebouncedInput
                value={field.value}
                onChange={field.onChange}
                placeholder="답글을 입력해주세요..."
                maxLength={1000}
                delay={300}
                className="w-full"
                type="textarea"
                rows={4}
              />
            )}
          />
          <div className="flex justify-between mt-1">
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description.message}</p>
            )}
            <p className="text-xs text-gray-500 ml-auto">
              {watchedDescription.length}/1000
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            첨부 이미지 (선택사항)
          </label>
          <div className="space-y-3">
            <div className="flex items-center justify-center w-full">
              <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isUploading 
                  ? "border-teal-300 bg-teal-50" 
                  : "border-gray-300 bg-gray-50 hover:bg-gray-100"
              }`}>
                <div className="flex items-center gap-2">
                  {isUploading ? (
                    <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5 text-gray-500" />
                  )}
                  <span className="text-sm text-gray-500">
                    {isUploading ? "업로드 중..." : "이미지 첨부"}
                  </span>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  className="hidden"
                />
              </label>
            </div>

            {images.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ImageIcon className="w-4 h-4" />
                  <span>첨부된 이미지 ({images.length}개)</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.preview}
                        alt={img.file.name}
                        className="w-full h-16 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors text-xs opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={isLoading || isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 transition-colors"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {isLoading ? "작성 중..." : "답글 작성"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ODOCommentForm;
